import React from 'react';
import { Sparkles, ArrowRight, Lock, Shield } from 'lucide-react';
import { useTenant } from '../context/TenantContext';
import { useProductMode } from '../context/ProductModeContext';

// Upgrade CTA Banner - shown when feature is not available on current tier
// RBAC FIX: Admins bypass tier restrictions - never see commercial upsells
export const UpgradeBanner = ({ feature = 'in-app editing', className = '' }) => {
  const { getTier } = useTenant();
  const { isAdminBypass } = useProductMode();
  const tier = getTier();
  
  // RBAC FIX: Admins never see upgrade banners
  if (isAdminBypass) {
    return null;
  }
  
  return (
    <div className={`bg-[var(--clr-brand-primary)] bg-opacity-5 border border-[var(--clr-brand-primary)] border-opacity-20 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[var(--clr-brand-primary)] bg-opacity-10 rounded-lg">
            <Sparkles className="w-5 h-5 text-[var(--clr-brand-primary)]" />
          </div>
          <div>
            <p className="font-medium text-[var(--clr-text-primary)]">Upgrade to unlock {feature}</p>
            <p className="text-sm text-[var(--clr-text-secondary)]">
              You're on the <span className="font-medium capitalize">{tier}</span> plan. 
              Upgrade to Business or Enterprise to edit data in-app.
            </p>
          </div>
        </div>
        <button 
          className="flex items-center gap-2 px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] whitespace-nowrap"
          onClick={() => window.location.href = '/admin/billing'}
        >
          Upgrade <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Inline upgrade hint for specific elements
// RBAC FIX: Admins bypass tier restrictions
export const UpgradeHint = ({ children, feature = 'editing' }) => {
  const { canEdit } = useTenant();
  const { isAdminBypass } = useProductMode();
  
  // RBAC FIX: Admins always have access
  if (isAdminBypass || canEdit()) {
    return children;
  }
  
  return (
    <div className="relative group">
      <div className="opacity-50 pointer-events-none">
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-gray-900/10 rounded opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="bg-white px-3 py-1.5 rounded-lg shadow-lg text-sm flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-[var(--clr-brand-primary)]" />
          <span>Upgrade to enable {feature}</span>
        </div>
      </div>
    </div>
  );
};

// Button wrapper that shows upgrade prompt when feature is gated
// RBAC FIX: Admins bypass tier restrictions
export const GatedButton = ({ 
  children, 
  onClick, 
  feature = 'this feature',
  className = '',
  ...props 
}) => {
  const { canEdit, getTier } = useTenant();
  const { isAdminBypass } = useProductMode();
  
  // RBAC FIX: Admins always have access
  if (isAdminBypass || canEdit()) {
    return (
      <button onClick={onClick} className={className} {...props}>
        {children}
      </button>
    );
  }
  
  return (
    <button
      className={`${className} opacity-60 cursor-not-allowed relative group`}
      onClick={(e) => {
        e.preventDefault();
        // Show tooltip or modal
      }}
      title={`Upgrade to Business or Enterprise to use ${feature}`}
      {...props}
    >
      <Lock className="w-3.5 h-3.5 mr-1 inline" />
      {children}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
        <Sparkles className="w-3 h-3 inline mr-1" />
        Upgrade to {getTier() === 'starter' ? 'Business' : 'Business'} to unlock
      </div>
    </button>
  );
};

// Edit icon that shows lock when gated
// RBAC FIX: Admins bypass tier restrictions
export const GatedEditIcon = ({ className = 'w-4 h-4' }) => {
  const { canEdit } = useTenant();
  const { isAdminBypass } = useProductMode();
  
  // RBAC FIX: Admins always have access
  if (isAdminBypass || canEdit()) {
    return null; // Will be replaced with actual edit icon in parent
  }
  
  return <Lock className={`${className} text-[var(--clr-text-muted)]`} />;
};

// HOC to wrap entire sections
export const withFeatureGate = (WrappedComponent, featureName = 'manual_editing') => {
  return function GatedComponent(props) {
    const { hasFeature } = useTenant();
    
    if (!hasFeature(featureName)) {
      return (
        <div className="relative">
          <div className="opacity-50 pointer-events-none">
            <WrappedComponent {...props} />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <UpgradeBanner feature="this feature" />
          </div>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
};

export default UpgradeBanner;
